import { memo } from "react";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import { CardSelectProps } from "./types";

const CardSelect: React.FC<CardSelectProps> = ({
  options,
  value,
  onChange,
  columns = 2,
  error,
}) => {
  // Jedan stupac je popis: stavke su spojene, a linije između njih dijele jedan rub.
  const isList = columns === 1;

  return (
    <div>
      <div
        className={classNames(
          "grid",
          isList
            ? classNames("rounded-sm border divide-y", error ? "border-red-400 divide-red-200" : "border-slate-300 divide-slate-300")
            : "gap-2"
        )}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {options.map((option) => {
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={classNames(
                // Ring umjesto bordera: ne zauzima prostor u rasporedu, pa deblji obrub
                // odabrane kartice ne pomiče susjedne.
                "group relative flex items-center gap-3 p-4 cursor-pointer",
                isList ? "" : "rounded-sm",
                // Bez ovoga preglednik nakon klika crta svoj outline, i to IZVAN kartice —
                // pa odabrana kartica izgleda šire od susjednih.
                "focus:outline-none",
                // Animira se samo ring i pozadina. `transition-all` je animirao i
                // `outline-color`, pa je gašenje outlinea izgledalo kao crni bljesak.
                "transition-[box-shadow,background-color] duration-200",
                isList
                  // U popisu odabrana stavka nosi obrub izvana i iznad susjeda: prekriva njihove
                  // linije i rub popisa, a svoju liniju skriva, pa uz plavi nema sivog ruba.
                  ? isSelected
                    ? "z-10 ring-2 ring-blue-400 bg-slate-50 !border-transparent"
                    : error
                      ? "ring-inset bg-red-50 hover:ring-2 hover:ring-red-400"
                      : "ring-inset bg-white hover:bg-slate-50"
                  : isSelected
                    ? "ring-inset ring-2 ring-blue-400 bg-slate-50"
                    // Greška boji sve kartice: javlja se kad odabira nema, pa nema jedne
                    // krive koju bi se istaknulo — nedostaje sam izbor.
                    : error
                      ? "ring-inset ring-1 ring-red-400 bg-red-50 hover:ring-2 hover:ring-red-400"
                      : "ring-inset ring-1 ring-slate-300 bg-white hover:ring-2 hover:ring-blue-200 "
              )}
            >
              {option.icon && (
                <div className={classNames(
                  "w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all",
                  isSelected ? "bg-blue-100" : "bg-slate-100"
                )}>
                  <ReactSVG
                    src={option.icon}
                    className={classNames("w-5 h-5", isSelected ? "stroke-blue-600" : "stroke-slate-600")}
                  />
                </div>
              )}
              <div className="text-left">
                {/* Naslov nosi boju odabira, opis ostaje prigušen — inače se kartica čita
                    kao dva jednako važna retka. */}
                <h3
                  className={classNames(
                    "text-xs uppercase tracking-wide font-semibold transition-colors",
                    isSelected ? "text-blue-700" : "text-slate-600 group-hover:text-blue-700"
                  )}
                >
                  {option.title}
                </h3>
                {option.description && (
                  <p
                    className={classNames(
                      "text-xs leading-relaxed transition-colors",
                      isSelected ? "text-slate-600" : "text-slate-500"
                    )}
                  >
                    {option.description}
                  </p>
                )}
              </div>
              <div className="ml-auto shrink-0">
                <div
                  className={classNames(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                    isSelected
                      ? "border-blue-500 bg-blue-500"
                      : "border-slate-300 bg-white"
                  )}
                >
                  {isSelected && (
                    <svg
                      className="w-3 h-3 text-white"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 6l3 3 5-5" />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default memo(CardSelect);
